import { useState } from 'react'
import { setUserCapabilities } from '../../api/auth.ts'
import { isApiError } from '../../api/client.ts'
import type { User } from '../../types/auth.ts'
import { CAPABILITY_GROUPS, type Capability } from '../../utils/capabilities.ts'

const KNOWN = new Set<string>(
  CAPABILITY_GROUPS.flatMap(g => g.items.map(i => i.capability)),
)

/** Admin role editor (Users page): check the roles a user should hold and
 *  save. The server grants/revokes the difference and audits each change.
 *  Grants outside the known vocabulary are neither shown nor touched. */
export function RolesDialog({
  target,
  onClose,
  onSaved,
}: {
  target: User
  onClose: () => void
  onSaved: (updated: User) => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((target.capabilities ?? []).filter(c => KNOWN.has(c))),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggle(cap: Capability) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(cap)) next.delete(cap)
      else next.add(cap)
      return next
    })
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const updated = await setUserCapabilities(target.id, [...selected].sort())
      onSaved(updated)
    } catch (err) {
      setError(isApiError(err) ? err.detail : 'Failed to save roles')
      setSaving(false)
    }
  }

  const adminImpliesDispatch = selected.has('oms.admin')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-labelledby="roles-dialog-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-auto"
      >
        <h3 id="roles-dialog-title" className="text-base font-semibold text-gray-800 mb-1">
          Roles
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          What <span className="font-medium text-gray-700">{target.username}</span> can do.
          Changes apply to their next action; their screens update when they reload the page.
        </p>

        {error && <div className="text-sm text-red-700 bg-red-50 p-2 rounded mb-3">{error}</div>}

        <div className="space-y-4">
          {CAPABILITY_GROUPS.map(group => (
            <fieldset key={group.title}>
              <legend className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {group.title}
              </legend>
              <div className="space-y-2">
                {group.items.map(item => {
                  const implied = item.capability === 'oms.dispatch' && adminImpliesDispatch
                  return (
                    <label key={item.capability} className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={implied || selected.has(item.capability)}
                        disabled={implied || saving}
                        onChange={() => toggle(item.capability)}
                      />
                      <span>
                        <span className="text-gray-800">{item.label}</span>
                        {implied && (
                          <span className="text-gray-400"> (included with Outage admin)</span>
                        )}
                        <span className="block text-xs text-gray-500">{item.description}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="flex gap-3 pt-5">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex-1 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save roles'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
